import { Api } from "src/utils/api";
import { TOTAL_SALES_API_BASE_URL } from "./total-sales.constant";

export interface WidgetData {
  totalUnitSold: number;
  totalSales: number;
  totalSpend: number;
  totalClicks: number;
  totalOrders: number;
  totalCvr: number;
  totalTacos: number;
  totalMer: number;
  totalNetProceeds: number;
  totalContributionsProfit: number;
  totalContributionsMargin: number;
  totalGrossMargin: number;
  totalRoi: number;
}

export interface WidgetDataResponse {
  total_unit_sold?: number;
  total_sales?: number;
  total_spend?: number;
  total_clicks?: number;
  total_orders?: number;
  total_cvr?: number;
  total_tacos?: number;
  total_mer?: number;
  total_net_proceeds?: number;
  total_contributions_profit?: number;
  total_contributions_margin?: number;
  total_gross_margin?: number;
  total_roi?: number;
}

export class TotalSalesApi extends Api {
  constructor() {
    super(TOTAL_SALES_API_BASE_URL);
  }

  public getTotalUnitsSold = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("units-sold", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getTotalSales = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("sales", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getTotalSpend = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("spend", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getTotalClicks = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("clicks", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getTotalOrders = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("orders", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getTotalCvr = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("orders", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getNetProceeds = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("net-proceeds", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getContributionsProfit = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("contribution-profit", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getContributionsMargin = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("contribution-margin", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getGrossMargin = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("gross-margin", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getRoi = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<number> => {
    const result = await this.get<number[]>("roi", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getAll = async (
    date_start: string,
    date_end: string,
    asin: string | null = null,
    cogs: number = 0,
    amazon_fees: number = 0,
    misc: number = 0,
  ): Promise<WidgetDataResponse> => {
    const result = await this.get<WidgetDataResponse>("all", {
      date_start,
      date_end,
      asin,
      cogs,
      amazon_fees,
      misc,
    });
    return Array.isArray(result) ? result[0] : result;
  };

  public getWidgetDataPromise = async (
    date_start: string,
    date_end: string,
  ): Promise<Partial<WidgetData>> => {
    const [
      totalUnitSold,
      totalSales,
      totalContributionsProfit,
      totalContributionsMargin,
      totalGrossMargin,
      totalRoi,
    ] = await Promise.all([
      this.getTotalUnitsSold(date_start, date_end),
      this.getTotalSales(date_start, date_end),
      this.getContributionsProfit(date_start, date_end),
      this.getContributionsMargin(date_start, date_end),
      this.getGrossMargin(date_start, date_end),
      this.getRoi(date_start, date_end),
    ]);

    return {
      totalUnitSold,
      totalSales,
      totalContributionsProfit,
      totalContributionsMargin,
      totalGrossMargin,
      totalRoi,
    };
  };
}
